import type { TestPlan } from "@/services/testing";
import type { ChatState } from "@/stores/testingStore";

/**
 * The Testing session model.
 *
 * A "session" is the conversation that produced and iterates ONE test. Almost always its artifact is
 * a server-side plan, so a plan row IS a session row — but not always: a chat that has messages and
 * has not yet produced a proposal lives only in the store under a `new:<ts>` scope. Those drafts used
 * to be listed nowhere, which made them permanently unreachable the moment the user clicked away.
 *
 * This module is the single place that turns (plans + chat scopes) into one recency-ordered list, so
 * the sidebar renders it and does not re-derive it.
 */

export type SessionDot = "ok" | "err" | "warn" | "busy" | "neutral";

export interface TestingSession {
	/** Stable React key + selection id: the plan id, or the scope key for a draft. */
	id: string;
	kind: "plan" | "draft";
	/** Chat scope this session's thread lives under. */
	scopeKey: string;
	/**
	 * The server-side conversation this plan belongs to (`test_plans.chat_session_id`). This is the
	 * DURABLE grouping key: unlike `scopeKey` (derived from the in-memory chat store, which is empty
	 * after a reload) it survives reloads and other devices. Null for drafts and legacy plans.
	 */
	chatSessionId: string | null;
	/** Null for a draft that has not produced a plan yet. */
	planId: string | null;
	title: string;
	/** Plan LIFECYCLE status (draft/proposed/active/…), or "draft". NOT the run outcome. */
	status: string;
	/** Authoritative run outcome from the plan's `last_run_status` — drives the All/Passing/Failing/
	 *  Not-run filter buckets and the status dot. The lifecycle `status` above never carries a
	 *  pass/fail, so filtering on it bucketed every run as "not-run". */
	verdict: "passing" | "failing" | "not-run";
	dot: SessionDot;
	/** Epoch ms, used only for ordering. */
	updatedAt: number;
	messageCount: number;
	/** This session owns the run that is currently streaming. */
	live: boolean;
}

/** Scope keys for not-yet-promoted chats are minted as `new:<Date.now()>`. */
const DRAFT_SCOPE_PREFIX = "new:";
/** Scope keys for promoted chats are `plan:<planId>`. */
const PLAN_SCOPE_PREFIX = "plan:";

function planUpdatedAt(plan: TestPlan): number {
	const raw = (plan as { updated_at?: string; created_at?: string }).updated_at ?? plan.created_at;
	const ts = raw ? Date.parse(String(raw)) : Number.NaN;
	return Number.isFinite(ts) ? ts : 0;
}

/**
 * Earliest timestamp a real scope key can carry. Keys are minted as `new:${Date.now()}`, so anything
 * below this is a malformed or legacy key — accepting it rendered drafts as "20671d ago" and filed
 * them under "Older", which is exactly where a brand-new draft must not go.
 */
const PLAUSIBLE_EPOCH_FLOOR = Date.UTC(2020, 0, 1);

/** A draft scope carries its creation time in its key. `fallback` covers a key we cannot trust. */
function draftCreatedAt(scopeKey: string, fallback: number): number {
	const ts = Number(scopeKey.slice(DRAFT_SCOPE_PREFIX.length));
	return Number.isFinite(ts) && ts >= PLAUSIBLE_EPOCH_FLOOR ? ts : fallback;
}

/**
 * Run outcome from the plan's authoritative `last_run_status`: 'passed' → passing; 'drift'/'regression'
 * (ran, didn't pass) → failing; null/absent (never ran) → not-run. Server truth, not client state.
 */
export function runVerdict(
	lastRunStatus: string | null | undefined,
): "passing" | "failing" | "not-run" {
	if (lastRunStatus === "passed") return "passing";
	if (lastRunStatus == null || lastRunStatus === "") return "not-run";
	return "failing";
}

/** Status dot: run outcome first (passing/failing), else the lifecycle status. */
function dotForSession(verdict: "passing" | "failing" | "not-run", status: string): SessionDot {
	if (verdict === "passing") return "ok";
	if (verdict === "failing") return "err";
	if (status === "rejected") return "err";
	if (status === "proposed" || status === "edited") return "warn";
	return "neutral";
}

/**
 * The chat scope that owns a plan's thread. A plan proposed inside a draft scope keeps that scope
 * until it is promoted, so the owning scope is whichever thread actually contains the proposal —
 * falling back to the canonical `plan:<id>` key.
 */
export function owningScopeForPlan(planId: string, byScope: Record<string, ChatState>): string {
	const found = Object.keys(byScope).find((key) =>
		byScope[key]?.messages.some((m) => m.proposedPlan?.id === planId),
	);
	return found ?? `${PLAN_SCOPE_PREFIX}${planId}`;
}

/**
 * Builds the recency-ordered session list.
 *
 * Ordering is by `updatedAt` descending, deliberately NOT grouped by outcome: status grouping made
 * rows jump between groups under the user's cursor whenever a run finished. Status survives as the
 * dot and as a filter.
 */
export function buildSessionList({
	plans,
	byScope,
	liveRunPlanId,
	runActive,
	now = Date.now(),
}: {
	plans: TestPlan[];
	byScope: Record<string, ChatState>;
	/** The plan whose run is currently streaming, if any. */
	liveRunPlanId: string | null;
	/** Whether that run is actually still in flight. */
	runActive: boolean;
	/** Injected for testability; also the fallback for a draft whose key carries no usable time. */
	now?: number;
}): TestingSession[] {
	const planSessions: TestingSession[] = plans.map((plan) => {
		const scopeKey = owningScopeForPlan(plan.id, byScope);
		const verdict = runVerdict(plan.last_run_status);
		return {
			id: plan.id,
			kind: "plan",
			scopeKey,
			chatSessionId: plan.chat_session_id ?? null,
			planId: plan.id,
			title: plan.name || "Untitled test",
			status: plan.status,
			verdict,
			dot:
				runActive && liveRunPlanId === plan.id ? "busy" : dotForSession(verdict, plan.status),
			updatedAt: planUpdatedAt(plan),
			messageCount: byScope[scopeKey]?.messages.length ?? 0,
			live: runActive && liveRunPlanId === plan.id,
		};
	});

	// Drafts: a `new:` scope with messages that has not produced any proposal yet. Once it proposes,
	// the store promotes the scope to `plan:<id>` and the plan row above represents it instead.
	const draftSessions: TestingSession[] = Object.keys(byScope)
		.filter((key) => key.startsWith(DRAFT_SCOPE_PREFIX))
		.filter((key) => {
			const state = byScope[key];
			return (
				!!state &&
				state.messages.length > 0 &&
				!state.messages.some((m) => m.proposedPlan) &&
				state.lastProposedPlanId == null
			);
		})
		.map((key) => {
			const state = byScope[key];
			const firstUserMessage = state.messages.find((m) => m.role === "user")?.content ?? "";
			return {
				id: key,
				kind: "draft" as const,
				scopeKey: key,
				chatSessionId: null,
				planId: null,
				title: firstUserMessage.trim() || "New test",
				status: "draft",
				verdict: "not-run" as const,
				dot: "neutral" as SessionDot,
				updatedAt: draftCreatedAt(key, now),
				messageCount: state.messages.length,
				live: false,
			};
		});

	return [...planSessions, ...draftSessions].sort((a, b) => b.updatedAt - a.updatedAt);
}

/**
 * A CONVERSATION — the thing the user means by "a session".
 *
 * `buildSessionList` returns one row per PLAN; but a single chat thread can propose several plans
 * (each a refinement), and the user thinks of that whole thread as one session that happens to hold N
 * plans. This is that grouping: rows sharing a chat scope collapse into one conversation. The right
 * "History" pane lists these (conversations, not plans), and the left pane shows only the plans of the
 * one that is open.
 */
export interface TestingSessionGroup {
	/** Session identity: the chat scope every plan in this conversation shares. */
	scopeKey: string;
	kind: "plan" | "draft";
	title: string;
	/** The plans proposed in this conversation, oldest → newest. Empty for a draft. */
	plans: TestingSession[];
	planCount: number;
	/** Most recent activity across the conversation — drives ordering and the recency buckets. */
	updatedAt: number;
	/** Worst outcome across the conversation's plans: failing ▸ passing ▸ not-run. */
	verdict: "passing" | "failing" | "not-run";
	dot: SessionDot;
	/** A plan in this conversation is running right now. */
	live: boolean;
	/** Messages in the thread — shown for a draft that has no plan yet. */
	messageCount: number;
}

/** Worst-first so a conversation with any failing plan reads as failing at a glance. */
function aggregateVerdict(plans: TestingSession[]): "passing" | "failing" | "not-run" {
	if (plans.some((p) => p.verdict === "failing")) return "failing";
	if (plans.some((p) => p.verdict === "passing")) return "passing";
	return "not-run";
}

/**
 * Collapses the flat per-plan rows from {@link buildSessionList} into conversations.
 *
 * Grouping key is `scopeKey` — the chat thread a plan was proposed in. A thread is promoted to
 * `plan:<firstPlanId>` on its first proposal and every later plan stays in it, so all of a
 * conversation's plans share that one key. The conversation is titled after its anchoring plan (the
 * one named in the scope key) so the title is stable as later plans are added; a draft keeps its own
 * title. Ordered most-recently-touched first, same as the plan list.
 */
export function buildSessions(rows: TestingSession[]): TestingSessionGroup[] {
	// Group on the DURABLE server key (`chatSessionId`) when the plan has one, so a conversation's plans
	// stay together across reloads; fall back to the in-memory `scopeKey` for drafts and legacy plans.
	const byKey = new Map<string, TestingSession[]>();
	for (const row of rows) {
		const key = row.chatSessionId ?? row.scopeKey;
		const list = byKey.get(key);
		if (list) list.push(row);
		else byKey.set(key, [row]);
	}

	const groups: TestingSessionGroup[] = [];
	for (const members of byKey.values()) {
		const isDraft = members.every((m) => m.kind === "draft");
		const plans = members
			.filter((m) => m.kind === "plan")
			.sort((a, b) => a.updatedAt - b.updatedAt);
		const live = members.some((m) => m.live);
		const verdict = aggregateVerdict(plans);
		// The earliest plan anchors the conversation — names it (stable as later plans are added) and,
		// via its client chat scope, is the thread selecting the session opens. A draft uses its own row.
		const anchor = plans[0] ?? members[0];
		groups.push({
			scopeKey: anchor?.scopeKey ?? members[0].scopeKey,
			kind: isDraft ? "draft" : "plan",
			title: anchor?.title ?? "Untitled test",
			plans,
			planCount: plans.length,
			updatedAt: members.reduce((max, m) => Math.max(max, m.updatedAt), 0),
			verdict,
			dot: live ? "busy" : verdict === "failing" ? "err" : verdict === "passing" ? "ok" : "neutral",
			live,
			messageCount: members.reduce((max, m) => Math.max(max, m.messageCount), 0),
		});
	}
	return groups.sort((a, b) => b.updatedAt - a.updatedAt);
}

/**
 * The conversation the user is currently looking at. Resolved by the SELECTED PLAN first (the group
 * that contains it) so it works even after a reload where the active chat scope is just `plan:<id>`;
 * falls back to matching a draft thread by its scope key.
 */
export function activeSessionOf(
	groups: TestingSessionGroup[],
	activeScopeKey: string | null,
	selectedPlanId?: string | null,
): TestingSessionGroup | null {
	if (selectedPlanId) {
		const owning = groups.find((g) => g.plans.some((p) => p.planId === selectedPlanId));
		if (owning) return owning;
	}
	if (!activeScopeKey) return null;
	return groups.find((g) => g.scopeKey === activeScopeKey) ?? null;
}

/** Buckets the recency-grouped lists are split into, in display order. */
export type SessionGroupKey = "active" | "today" | "yesterday" | "week" | "older";

const GROUP_LABELS: Record<SessionGroupKey, string> = {
	active: "Active now",
	today: "Today",
	yesterday: "Yesterday",
	week: "Previous 7 days",
	older: "Older",
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** Midnight local time for the day `offsetDays` before `now`. */
function startOfDay(now: number, offsetDays: number): number {
	const d = new Date(now);
	d.setHours(0, 0, 0, 0);
	return d.getTime() - offsetDays * DAY_MS;
}

/**
 * Splits a recency-ordered list into date buckets, with anything currently executing lifted into its
 * own "Active now" group at the top. Generic over anything carrying `updatedAt` + `live`, so the left
 * plan list and the History conversation list split into identical buckets from one implementation.
 *
 * Date headers exist because "where did my previous session go?" is a scanning problem: a flat list of
 * similarly-shaped rows gives the eye nothing to anchor on. Empty buckets are dropped, so three tests
 * from today show one header, not five. `now` is injected to keep the function pure and testable.
 */
export function bucketByRecency<T extends { updatedAt: number; live: boolean }>(
	items: T[],
	now: number = Date.now(),
): Array<{ key: SessionGroupKey; label: string; items: T[] }> {
	const todayStart = startOfDay(now, 0);
	const yesterdayStart = startOfDay(now, 1);
	const weekStart = startOfDay(now, 7);

	const buckets: Record<SessionGroupKey, T[]> = {
		active: [],
		today: [],
		yesterday: [],
		week: [],
		older: [],
	};

	for (const item of items) {
		if (item.live) {
			buckets.active.push(item);
		} else if (item.updatedAt >= todayStart) {
			buckets.today.push(item);
		} else if (item.updatedAt >= yesterdayStart) {
			buckets.yesterday.push(item);
		} else if (item.updatedAt >= weekStart) {
			buckets.week.push(item);
		} else {
			buckets.older.push(item);
		}
	}

	return (Object.keys(buckets) as SessionGroupKey[])
		.filter((key) => buckets[key].length > 0)
		.map((key) => ({ key, label: GROUP_LABELS[key], items: buckets[key] }));
}
